
import {defineComponent} from 'vue'
import {UIStateDto} from "@/dtos/UIStateDto";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {CodeDescriptionRestDto, DeliveryMethodSettingsRestDto} from "@/api/pharma-order-settings/models";
import {DeliveryMethodsRestService} from "@/services/rest/settings/DeliveryMethodsRestService";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {useToast} from "vue-toastification";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {ProductFilterOverviewRestDto} from "@/api/pharma-cpc-mgmt/models";
import {ProductFiltersRestService} from "@/services/rest/product-filters/ProductFiltersRestService";
import {AuthContext} from "@/context/AuthContext";

export default defineComponent({
  name: "DeliveryMethods",
  computed: {
    AuthContext() {
      return AuthContext
    }
  },
  components: {
    BaseContainer,
    BaseTitle,
    AlertError2,
  },
  data() {
    return {
      authorizedMethods: [] as CodeDescriptionRestDto[],
      supportedMethodCodes: [] as string[],
      deliveryMethodSettings: [] as DeliveryMethodSettingsRestDto[],
      productFilters: [] as ProductFilterOverviewRestDto[],

      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.reloadContent();
    this.pageUI.setReady();
  },
  methods: {
    async reloadContent(): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        this.authorizedMethods = await DeliveryMethodsRestService.getInstance().findAuthorizedFlowDeliveryMethods();
        this.supportedMethodCodes = await DeliveryMethodsRestService.getInstance().findSupportedDeliveryMethodCodes();
        this.deliveryMethodSettings = await DeliveryMethodsRestService.getInstance().findDeliveryMethodSettings();
        this.productFilters = await ProductFiltersRestService.getInstance().findProductFilters(true);
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    isActive(methodCode: string): boolean {
      return this.supportedMethodCodes.includes(methodCode);
    },
    toggleIsActive(methodCode: string): void {
      if (this.isActive(methodCode)) {
        this.supportedMethodCodes = this.supportedMethodCodes.filter(item => item !== methodCode);
      } else {
        this.supportedMethodCodes.push(methodCode);
      }
    },
    async submitData(): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        await DeliveryMethodsRestService.getInstance()
          .synchronizeSupportedDeliveryMethods(this.supportedMethodCodes);

        await handleSavedSuccessfully();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    getFilterName(filterCode: string): string {
      return this.productFilters.find(item => item.code === filterCode)?.name ?? '';
    },
    getDeliveryMethodSettings(code: string) {
      const settings: DeliveryMethodSettingsRestDto | undefined =
        this.deliveryMethodSettings.find(item => item.delivery_method_code === code);

      if (!settings) {
        return '&nbsp;'
      }

      const remarks: string[] = [];

      if (settings.is_allowed_as_guest) {
        remarks.push(this.$t('alsoAllowedAsGuest'));
      }
      if (settings.is_checkout_payment_required) {
        remarks.push(this.$t('checkoutPaymentRequired'));
      }
      if (settings.restricted_to_product_filter_code) {
        remarks.push(this.$t(
          'restrictedToProductFilter',
          {productFilter: this.getFilterName(settings.restricted_to_product_filter_code)}
        ))
      }

      if (remarks.length === 0) {
        return '&nbsp;'
      }

      return `(${remarks.join(', ')})`;
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    }
  }
})
